import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import { FormSaveBar } from '../../../components/FormSaveBar/FormSaveBar'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { PermissionsSaveBar } from './Components/PermissionsSaveBar'

import { getScopeAccessType, groupPermissionsByScopeCategory, type ScopeAccessType } from '../../../common/scopeCategories'
import { COMBINED_ROLE_GROUPS, getCoveredRoleGroups } from './utils'

import { type OAuth2App, useGetAccountAppScopesQuery, useSetAccountAppScopesMutation } from '../../../store/auth/auth'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useListPermissionsQuery } from '../../../store/permissions/permissions'
import { addToast } from '../../../store/toast/toastSlice'

import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { useGetRolesQuery } from '../../../store/role/role'
import './OAuth2AppsPermissionsTab.scss'
import { useOAuth2AppRolePermissions } from './hooks'

type FormValues = {
  roleName: string
}

type ScopeEditState = {
  editingAppId: string | null
  enabledScopes: Set<string>
  originalScopes: Set<string>
  isConfirming: boolean
}

const INITIAL_SCOPE_EDIT_STATE: ScopeEditState = {
  editingAppId: null,
  enabledScopes: new Set(),
  originalScopes: new Set(),
  isConfirming: false,
}

interface PermissionAccessBadgeProps {
  access: ScopeAccessType
  dimmed?: boolean
}

const PermissionAccessBadge: React.FC<PermissionAccessBadgeProps> = ({ access, dimmed }) => (
  <div
    className={classNames('d-flex py-1 px-2 justify-content-center align-items-center zocks-gap-0.25 zocks-border-0.5', {
      'zocks-text-gray-600 zocks-bg-color-gray-200': dimmed,
      'zocks-text-primary-dark zocks-bg-color-primary-light': !dimmed && access === 'read',
      'zocks-text-warning-text zocks-bg-color-warning': !dimmed && access !== 'read',
    })}
  >
    {access === 'read' ? 'Read' : 'Write'}
  </div>
)

interface OAuth2AppsPermissionRowProps {
  scope: string
  description: string
  isEditMode?: boolean
  isChecked?: boolean
  onToggle?: (scope: string) => void
}

const OAuth2AppsPermissionRow: React.FC<OAuth2AppsPermissionRowProps> = ({ scope, description, isEditMode, isChecked, onToggle }) => {
  const isUnchecked = !isChecked
  return (
    <Row
      className={classNames('apps-permissions-row-box py-3 px-4 mx-0 my-3 zocks-gap-2 align-items-center', {
        'apps-permissions-row-box--edit': isEditMode,
        'apps-permissions-row-box--unchecked': isUnchecked,
      })}
    >
      {isEditMode && (
        <Col xs='auto' className='p-0 scope-checkbox-col'>
          <Form.Check type='checkbox' id={`scope-checkbox-${scope}`} checked={isChecked} onChange={() => onToggle?.(scope)} aria-label={description} />
        </Col>
      )}
      <Col xs={9} className='d-flex flex-column gap-1 p-0'>
        <div className={classNames('h6', { 'zocks-text-gray-600': isUnchecked })}>{description}</div>
        <div className={classNames('label-medium', isUnchecked ? 'zocks-text-gray-500' : 'zocks-text-gray-600')}>{scope}</div>
      </Col>
      <Col className='d-flex flex-column justify-content-center align-items-end p-0'>
        <PermissionAccessBadge access={getScopeAccessType(scope)} dimmed={isUnchecked} />
      </Col>
    </Row>
  )
}

export interface OAuth2AppsPermissionsTabProps {
  oAuth2App: OAuth2App
}

export const OAuth2AppsPermissionsTab: React.FC<OAuth2AppsPermissionsTabProps> = ({ oAuth2App }) => {
  const { scopes, permissions, id: appId, clientId } = oAuth2App
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()

  const initialSelectedRadioButton = COMBINED_ROLE_GROUPS[0].name

  const form = useForm<FormValues>({
    defaultValues: { roleName: initialSelectedRadioButton },
    mode: 'onChange',
  })

  const { control, handleSubmit, reset, formState } = form
  const { errors, isDirty, isSubmitting, isValid } = formState

  const { data: permissionsData, isLoading: permissionsIsLoading } = useListPermissionsQuery()

  const {
    data: rolesResponse,
    isLoading: isLoadingRoles,
    isFetching: isFetchingRoles,
  } = useGetRolesQuery(
    {
      accountId,
      pageRequest: { page: 0, size: 500 },
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const { updatePermissions, isUpdating } = useOAuth2AppRolePermissions({
    accountId,
    appId,
    accountEnabled: permissions.account,
    successToast: {
      title: 'Success',
      messages: ['Successfully updated.'],
    },
    errorToast: {
      messages: ['Failed to update app. Please try again.'],
    },
  })

  const { data: appScopesData, isLoading: isScopesLoading, isError: isScopesError } = useGetAccountAppScopesQuery({ accountId, appId })
  const [setAccountAppScopes, { isLoading: isSavingScopes }] = useSetAccountAppScopesMutation()

  const [scopeEditState, setScopeEditState] = useState<ScopeEditState>(INITIAL_SCOPE_EDIT_STATE)
  const [lastSeenAppId, setLastSeenAppId] = useState(appId)
  if (lastSeenAppId !== appId) {
    setLastSeenAppId(appId)
    setScopeEditState(INITIAL_SCOPE_EDIT_STATE)
  }
  const { editingAppId, enabledScopes, originalScopes, isConfirming } = scopeEditState
  const isEditMode = editingAppId === appId

  const isScopesDirty = useMemo(() => {
    if (enabledScopes.size !== originalScopes.size) {
      return true
    }
    for (const scope of enabledScopes) {
      if (!originalScopes.has(scope)) {
        return true
      }
    }
    return false
  }, [enabledScopes, originalScopes])

  const initialRoleName = useMemo(() => {
    const enabledSystemRoleIds = Object.entries(permissions.systemRoles)
      .filter(([, value]) => value)
      .map(([key]) => key)

    return getCoveredRoleGroups(enabledSystemRoleIds).find((roleGroup) => roleGroup.name)?.name ?? initialSelectedRadioButton
  }, [permissions.systemRoles, initialSelectedRadioButton])

  const filteredPermissions = useMemo(() => {
    if (!permissionsData?.data) {
      return []
    }
    const scopeSet = new Set(scopes)
    return permissionsData.data.filter((permission) => scopeSet.has(permission.id))
  }, [permissionsData, scopes])

  const groupedPermissions = useMemo(() => groupPermissionsByScopeCategory(filteredPermissions), [filteredPermissions])

  const readOnlyEnabledScopes = useMemo(() => {
    if (!appScopesData) {
      return null
    }
    return new Set(appScopesData.data.scopes)
  }, [appScopesData])

  const { hasQuota, validateQuotaValue } = useZocksQuota()
  const oauth2Enabled = hasQuota('integration.oauth2Enabled.value')
  const appNotBlacklisted = validateQuotaValue('integration.oauth2AppBlacklist', clientId)

  useEffect(() => {
    reset(
      { roleName: initialRoleName ?? initialSelectedRadioButton },
      {
        keepDirty: false,
        keepTouched: false,
      },
    )
  }, [initialRoleName, reset, initialSelectedRadioButton])

  const handleDiscard = () => {
    reset({ roleName: initialRoleName ?? initialSelectedRadioButton })
  }

  const onSubmit = async (values: FormValues) => {
    if (!rolesResponse) {
      dispatch(
        addToast({
          variant: 'error',
          title: 'Error',
          messages: ['Failed to update app. Please try again.'],
        }),
      )
      return
    }
    const roleIds = COMBINED_ROLE_GROUPS.find((roleGroup) => roleGroup.name === values.roleName)?.roleIds
    const result = await updatePermissions({
      selectedRoleIds: roleIds ?? [],
      allSystemRoles: rolesResponse.data.map((role) => role.id),
    })
    if (!result.ok) {
      return result
    }
    reset(values)
    return result
  }

  const handleEditClick = useCallback(() => {
    const currentScopes = appScopesData?.data.scopes
    if (!currentScopes) {
      dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to load app permissions. Please try again.'] }))
      return
    }
    setScopeEditState({ editingAppId: appId, enabledScopes: new Set(currentScopes), originalScopes: new Set(currentScopes), isConfirming: false })
  }, [appScopesData, appId, dispatch])

  const handleScopeToggle = useCallback((scope: string) => {
    setScopeEditState((prev) => {
      const next = new Set(prev.enabledScopes)
      if (next.has(scope)) {
        next.delete(scope)
      } else {
        next.add(scope)
      }
      return { ...prev, enabledScopes: next }
    })
  }, [])

  const handleDiscardScopes = useCallback(() => {
    setScopeEditState(INITIAL_SCOPE_EDIT_STATE)
  }, [])

  const handleSaveScopes = () => {
    setScopeEditState((prev) => ({ ...prev, isConfirming: true }))
  }

  const handleBackFromConfirm = () => {
    setScopeEditState((prev) => ({ ...prev, isConfirming: false }))
  }

  const handleConfirmScopes = useCallback(async () => {
    try {
      await setAccountAppScopes({ accountId, appId, scopes: Array.from(enabledScopes) }).unwrap()
      dispatch(addToast({ variant: 'success', title: 'Successfully updated' }))
      setScopeEditState(INITIAL_SCOPE_EDIT_STATE)
    } catch {
      dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to update permissions. Please try again.'] }))
    }
  }, [setAccountAppScopes, accountId, appId, enabledScopes, dispatch])

  return (
    <Container fluid className='px-0'>
      <div className='flex-grow-1 zocks-pb-10.0'>
        <ScopedBlock
          scope={{
            scope: 'oauth2_app:update',
            superScope: ['oauth2_app:admin'],
          }}
          missingScopeAction={MissingScopeAction.HIDE}
        >
          {oauth2Enabled && appNotBlacklisted && (
            <Form id='app-role-edit-form' onSubmit={handleSubmit(onSubmit)}>
              <Row className='m-0 edit-roles-box gap-4 mb-4'>
                <Row className='p-0'>
                  <h4>Roles with access</h4>
                </Row>

                <Row className='p-0 m-0 gap-2'>
                  <Row className='p-0 m-0'>
                    <Col className='p-0 m-0'>
                      <Controller
                        name='roleName'
                        control={control}
                        render={({ field }) => (
                          <>
                            {COMBINED_ROLE_GROUPS.map((group) => (
                              <Form.Check
                                key={group.name}
                                disabled={!permissions.account || isEditMode}
                                inline
                                type='radio'
                                className='pb-2'
                                label={group.name}
                                id={`modal_${group.name}`}
                                {...field}
                                value={group.name}
                                checked={field.value === group.name}
                              />
                            ))}
                          </>
                        )}
                      />
                    </Col>
                  </Row>
                </Row>
              </Row>
            </Form>
          )}
        </ScopedBlock>

        <Row className='mx-0'>
          <Col className='p-0 my-0'>
            <div className='d-flex align-items-center justify-content-between permissions-heading-row mb-3'>
              <h4 className='mb-0'>Permissions</h4>
              <ScopedBlock scope={{ scope: 'oauth2_app:update', superScope: ['oauth2_app:admin'] }} missingScopeAction={MissingScopeAction.HIDE}>
                <button
                  type='button'
                  className='btn-link-edit d-flex align-items-center gap-1 border-0 bg-transparent p-0'
                  onClick={handleEditClick}
                  disabled={isScopesLoading || isScopesError || isDirty || isEditMode}
                >
                  <i className='bi bi-pencil-square' />
                  <strong>Edit</strong>
                </button>
              </ScopedBlock>
            </div>

            {permissionsIsLoading || isScopesLoading ? (
              <div className='d-flex align-items-center justify-content-center'>
                <Spinner />
              </div>
            ) : (
              Array.from(groupedPermissions.entries()).map(([category, permissionsInGroup]) => {
                if (!permissionsInGroup.length) {
                  return null
                }
                return (
                  <div key={category} className='mb-4'>
                    <h6 className='mb-3'>{category}</h6>

                    {permissionsInGroup.map((permission) => (
                      <OAuth2AppsPermissionRow
                        key={permission.id}
                        scope={permission.id}
                        description={permission.description}
                        isEditMode={isEditMode}
                        isChecked={isEditMode ? enabledScopes.has(permission.id) : readOnlyEnabledScopes?.has(permission.id)}
                        onToggle={handleScopeToggle}
                      />
                    ))}
                  </div>
                )
              })
            )}
          </Col>
        </Row>
      </div>
      <FormSaveBar
        formId='app-role-edit-form'
        isVisible={isDirty}
        isSubmitting={isSubmitting || isUpdating}
        isValid={isValid && !isLoadingRoles && !isFetchingRoles && !!rolesResponse}
        isDirty={isDirty}
        errors={errors}
        onDiscard={handleDiscard}
        discardLabel={'Discard changes'}
        saveLabel={'Save changes'}
        defaultFormLabel={'You have unsaved changes.'}
      />
      <PermissionsSaveBar
        isVisible={isEditMode && !isDirty}
        isConfirming={isConfirming}
        isSubmitting={isSavingScopes}
        isDirty={isScopesDirty}
        onDiscard={handleDiscardScopes}
        onSave={handleSaveScopes}
        onBack={handleBackFromConfirm}
        onConfirm={handleConfirmScopes}
      />
    </Container>
  )
}
